import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Check, Sparkles, ArrowRight, Users } from 'lucide-react';
import { LandingNav, LpButton, LOGIN_PATH } from './LandingNav';
import { Footer } from './LandingPage';
import './landing.css';

/* When the launch sale ends, flip this to false: every captain plan then shows
   its real price as the active number instead of "Free". */
const LIMITED_TIME_PROMO = true;

type Tier = {
  name: string;
  teams: number;
  monthly: number;
  annual: number;
  featured?: boolean;
  features: string[];
};

const TIERS: Tier[] = [
  {
    name: 'Single Team Captain',
    teams: 1,
    monthly: 6,
    annual: 60,
    features: [
      'Manage 1 active team',
      'Manage players',
      'Organize matches',
      'Set lineups',
      'AI Co-Captain (limited use)',
    ],
  },
  {
    name: '3 Team Captain',
    teams: 3,
    monthly: 10,
    annual: 100,
    featured: true,
    features: [
      'Manage 3 active teams',
      'Everything included in Single Team Captain',
    ],
  },
  {
    name: '5 Team Captain',
    teams: 5,
    monthly: 15,
    annual: 150,
    features: [
      'Manage 5 active teams',
      'Everything included in Single Team Captain',
    ],
  },
];

type Billing = 'monthly' | 'annual';

const fmt = (n: number) => `$${n}`;

/* ---------- Billing toggle ---------- */
const BillingToggle: React.FC<{ billing: Billing; onChange: (b: Billing) => void }> = ({ billing, onChange }) => (
  <div className="lp-billing-toggle" role="tablist" aria-label="Billing period">
    <button
      type="button"
      role="tab"
      aria-selected={billing === 'monthly'}
      className={`lp-billing-opt${billing === 'monthly' ? ' is-active' : ''}`}
      onClick={() => onChange('monthly')}
    >
      Monthly
    </button>
    <button
      type="button"
      role="tab"
      aria-selected={billing === 'annual'}
      className={`lp-billing-opt${billing === 'annual' ? ' is-active' : ''}`}
      onClick={() => onChange('annual')}
    >
      Annual <span className="lp-billing-save">save ~17%</span>
    </button>
  </div>
);

/* ---------- Tier card ---------- */
const TierCard: React.FC<{ tier: Tier; billing: Billing; goLogin: () => void }> = ({ tier, billing, goLogin }) => {
  const price = billing === 'monthly' ? tier.monthly : tier.annual;
  const period = billing === 'monthly' ? '/mo' : '/yr';

  return (
    <div className={`lp-price-card${tier.featured ? ' lp-price-card--featured' : ''}`}>
      {tier.featured && <span className="lp-price-ribbon">Most popular</span>}
      <div className="lp-price-head">
        <h3 className="lp-price-name">{tier.name}</h3>
        <p className="lp-price-teams">Up to {tier.teams} {tier.teams === 1 ? 'team' : 'teams'}</p>
      </div>

      <div className="lp-price-block">
        {LIMITED_TIME_PROMO ? (
          <>
            <span className="lp-price-badge"><Sparkles size={13} /> Limited time</span>
            <div className="lp-price-amount">Free</div>
            <div className="lp-price-was">
              <s>{fmt(price)}{period}</s> while we launch
            </div>
          </>
        ) : (
          <div className="lp-price-amount">
            {fmt(price)}<span className="lp-price-period">{period}</span>
          </div>
        )}
      </div>

      <LpButton variant="accent" size="lg" onClick={goLogin} iconRight={<ArrowRight size={18} />}>
        {LIMITED_TIME_PROMO ? 'Start free' : 'Choose plan'}
      </LpButton>

      <ul className="lp-price-features">
        {tier.features.map((f) => (
          <li key={f}><Check size={16} /> {f}</li>
        ))}
      </ul>
    </div>
  );
};

/* ---------- Hero ---------- */
const PricingHero: React.FC<{ billing: Billing; setBilling: (b: Billing) => void }> = ({ billing, setBilling }) => (
  <section className="lp-section lp-section--tint lp-price-hero" id="pricing-top">
    <div className="lp-ball lp-ball--a" />
    <div className="lp-container">
      <div className="lp-section-head">
        <span className="lp-eyebrow">
          <Trophy size={15} /> {LIMITED_TIME_PROMO ? 'Free for a limited time' : 'Pricing'}
        </span>
        <h1 className="lp-h1" style={{ margin: '18px 0 0' }}>Pricing built for captains.</h1>
        <p className="lp-section-sub">
          Players are always free — invite your whole roster at no cost. Captains subscribe per team.
          {LIMITED_TIME_PROMO && <> Right now, <strong>every captain plan is free while we launch.</strong></>}
        </p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
        <BillingToggle billing={billing} onChange={setBilling} />
      </div>
    </div>
  </section>
);

/* ---------- Plans ---------- */
const Plans: React.FC<{ billing: Billing; goLogin: () => void }> = ({ billing, goLogin }) => (
  <section className="lp-section" id="pricing-plans">
    <div className="lp-container">
      <div className="lp-price-grid">
        {TIERS.map((tier) => (
          <TierCard key={tier.name} tier={tier} billing={billing} goLogin={goLogin} />
        ))}
      </div>
      <p className="lp-price-fineprint">Prices shown are introductory and subject to change.</p>
    </div>
  </section>
);

/* ---------- Players free band ---------- */
const PlayersFree: React.FC = () => (
  <section className="lp-players-free">
    <div className="lp-container lp-players-free-inner">
      <span className="lp-players-free-icon"><Users size={24} /></span>
      <div>
        <h2 className="lp-players-free-h">Players are always free</h2>
        <p className="lp-players-free-sub">
          Invite your entire roster at no cost. Only captains pay — and only for the teams they run.
        </p>
      </div>
    </div>
  </section>
);

/* ---------- FAQ ---------- */
const FAQS = [
  {
    q: 'What happens when the free period ends?',
    a: 'While we launch, every captain plan is free. When the promo ends, your plan moves to the standard price shown above — we’ll give you plenty of notice first, and you can cancel anytime.',
  },
  {
    q: 'Are players really free?',
    a: 'Yes. Players never pay. Invite your whole roster to set availability, see the schedule, and get lineup notifications at no cost.',
  },
  {
    q: 'Can I change tiers later?',
    a: 'Absolutely. Start with one team and upgrade to the 3- or 5-team captain plan whenever you take on more teams.',
  },
  {
    q: 'Do I need a credit card to start?',
    a: 'No card needed to get started during the launch promo. Just create your team and go.',
  },
];
const FAQ: React.FC = () => (
  <section className="lp-section lp-section--tint" id="pricing-faq">
    <div className="lp-container">
      <div className="lp-section-head">
        <span className="lp-kicker">FAQ</span>
        <h2 className="lp-h2">Questions, answered</h2>
      </div>
      <div className="lp-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="lp-faq-item">
            <h3 className="lp-faq-q">{q}</h3>
            <p className="lp-faq-a">{a}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Final CTA ---------- */
const FinalCTA: React.FC<{ goLogin: () => void }> = ({ goLogin }) => (
  <section className="lp-finalcta">
    <div className="lp-ball lp-ball--d" />
    <div className="lp-container lp-finalcta-inner">
      <h2 className="lp-finalcta-h">Ready to run your team like a pro?</h2>
      <p className="lp-finalcta-sub">Set up your team today — free for a limited time.</p>
      <div className="lp-hero-cta lp-hero-cta--center">
        <LpButton variant="accent" size="lg" onClick={goLogin} iconRight={<ArrowRight size={18} />}>Get started free</LpButton>
      </div>
    </div>
  </section>
);

/* ---------- Page ---------- */
const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const goLogin = () => navigate(LOGIN_PATH);
  const [billing, setBilling] = useState<Billing>('monthly');

  return (
    <div className="lp-page">
      <LandingNav atHome={false} />
      <PricingHero billing={billing} setBilling={setBilling} />
      <Plans billing={billing} goLogin={goLogin} />
      <PlayersFree />
      <FAQ />
      <FinalCTA goLogin={goLogin} />
      <Footer />
    </div>
  );
};

export default PricingPage;
